import React, { useState, useEffect, useRef } from 'react';

import { useConfigStore, useAppStore } from '../../stores';
import { getAuthClient } from '../../utils/auth';
import { buildYourTourApiUrl } from '../../utils/ApiHelpers';
import { getDataVersion } from '../../utils/dataVersions';
import { prefetchFloorPlans } from '../FloorPlansPage/prefetchFloorPlans';
import { markAmenitiesVisited } from './prefetchAmenities';
import { prefetchMedia } from '../MediaPage/prefetchMedia';

import AmenitiesPageList from './AmenitiesPageList';
import AmenitiesPageSkeleton from './AmenitiesPageSkeleton';

import '../../GlobalScss/grid.scss';

const auth = getAuthClient();

const AmenitiesPage = () => {
  const [items, setItems] = useState(null); // null indicates loading, [] means empty
  const [error, setError] = useState(null);
  const prefetchTriggeredRef = useRef(false);

  const config = useConfigStore((state) => state.config);
  const settings = useAppStore((state) => state.settings);
  const { communityNid, urlSlug } = config || {};
  const { mode } = settings || {};

  // Mark that user has visited this page (for prefetch optimization)
  useEffect(() => {
    markAmenitiesVisited();
  }, []);

  // Fetch amenities data using optimized custom endpoint
  useEffect(() => {
    const fetchData = async () => {
      if (!urlSlug) return;

      try {
        const version = await getDataVersion( urlSlug, 'amenities', undefined, mode );
        const params = {
          ...( mode ? { mode } : {} ),
          ...( version ? { v: version } : {} ),
        };
        const url = buildYourTourApiUrl( `/your_tour_api/amenities_page/${urlSlug}`, params );
        const response = await auth.fetchWithAuthentication(url, 'GET', null);

        if (response && !response.error) {
          // API returns data in the exact format React expects
          setItems(response.data || []);
          setError(null);
        } else {
          setItems([]);
          setError(response?.error || 'Failed to load amenities');
        }
      } catch (err) {
        console.error('Error fetching amenities:', err);
        setError(err);
        setItems([]);
      }

      // Prefetch Floor Plans and Media after amenities load (if not already visited)
      if (!prefetchTriggeredRef.current) {
        prefetchTriggeredRef.current = true;
        const prefetchCallback = () => {
          // Prefetch both page types in parallel for instant navigation
          prefetchFloorPlans(communityNid, urlSlug, mode);
          prefetchMedia(urlSlug, mode);
        };
        // Use requestIdleCallback with setTimeout fallback for Safari
        if (typeof requestIdleCallback !== 'undefined') {
          requestIdleCallback(prefetchCallback, { timeout: 2000 });
        } else {
          setTimeout(prefetchCallback, 100);
        }
      }
    };

    if (urlSlug) {
      fetchData();
    }
  }, [communityNid, urlSlug, mode]);

  // Optional: Show error state
  if (error) {
    return (
      <div className="content">
        <p>Unable to load amenities. Please try again later.</p>
      </div>
    );
  }

  const isLoading = items === null;

  return (
    <div className="content amenitiesPage">
      {isLoading ? (
        <AmenitiesPageSkeleton />
      ) : (
        <AmenitiesPageList items={items} />
      )}
    </div>
  );
};

export default AmenitiesPage;
